import React, { useMemo, useState } from 'react';
import {
    ConveyorDrawingApplyPayload,
    DrawingRowSelection,
    Equipment,
    ExtractedConveyorLayout,
    PredefinedStation,
    ProcessTemplate,
} from '../types';

type RowKind = 'equipment' | 'process';

interface RowDraft {
    block_name: string;
    kind: RowKind;
    target_id: string | null;
    quantity: number;
    match_confidence: number;
    removed: boolean;
    robot_codes: string[];
}

interface StationDraft {
    code: string;
    description: string | null | undefined;
    station_type_id: string;
    rows: RowDraft[];
}

interface DxfMatchingDialogProps {
    layout: ExtractedConveyorLayout;
    catalog: Equipment[];
    processTemplates: ProcessTemplate[];
    stationTypes: PredefinedStation[];
    onCancel: () => void;
    onApply: (payload: ConveyorDrawingApplyPayload) => Promise<void>;
}

const encodeTarget = (kind: RowKind, id: string): string => `${kind}:${id}`;

const decodeTarget = (
    value: string
): { kind: RowKind; id: string } | null => {
    const sep = value.indexOf(':');
    if (sep <= 0) return null;
    const kind = value.slice(0, sep);
    const id = value.slice(sep + 1);
    if ((kind !== 'equipment' && kind !== 'process') || !id) return null;
    return { kind, id };
};

const buildInitialDraft = (
    layout: ExtractedConveyorLayout,
    stationTypes: PredefinedStation[]
): StationDraft[] => {
    const fallbackType = stationTypes[0]?.id ?? '';
    return layout.stations.map((station) => {
        const robotsByType = new Map<string, string[]>();
        for (const robot of station.robots) {
            const key = robot.type_description ?? `Robot (${robot.type_code ?? '—'})`;
            const codes = robotsByType.get(key) ?? [];
            codes.push(robot.code);
            robotsByType.set(key, codes);
        }
        const rows: RowDraft[] = station.equipment.map((candidate) => {
            const isProcess = Boolean(candidate.matched_process_template_id);
            return {
                block_name: candidate.block_name,
                kind: isProcess ? 'process' : 'equipment',
                target_id: isProcess
                    ? (candidate.matched_process_template_id ?? null)
                    : (candidate.matched_equipment_id ?? null),
                quantity: candidate.quantity,
                match_confidence: candidate.match_confidence,
                removed: false,
                robot_codes: robotsByType.get(candidate.block_name) ?? [],
            };
        });
        return {
            code: station.code,
            description: station.description,
            station_type_id: station.suggested_station_type_id ?? fallbackType,
            rows,
        };
    });
};

const DxfMatchingDialog: React.FC<DxfMatchingDialogProps> = ({
    layout,
    catalog,
    processTemplates,
    stationTypes,
    onCancel,
    onApply,
}) => {
    const [stations, setStations] = useState<StationDraft[]>(() =>
        buildInitialDraft(layout, stationTypes)
    );
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const unmatchedCount = useMemo(
        () =>
            stations.reduce(
                (acc, station) =>
                    acc +
                    station.rows.filter(
                        (row) => !row.removed && !row.target_id
                    ).length,
                0
            ),
        [stations]
    );

    const equipmentLineCount = useMemo(
        () =>
            stations.reduce(
                (acc, station) =>
                    acc + station.rows.filter((row) => !row.removed).length,
                0
            ),
        [stations]
    );

    const updateStation = (idx: number, patch: Partial<StationDraft>) => {
        setStations((prev) =>
            prev.map((station, i) => (i === idx ? { ...station, ...patch } : station))
        );
    };

    const updateRow = (
        stationIdx: number,
        rowIdx: number,
        patch: Partial<RowDraft>
    ) => {
        setStations((prev) =>
            prev.map((station, i) =>
                i === stationIdx
                    ? {
                          ...station,
                          rows: station.rows.map((row, j) =>
                              j === rowIdx ? { ...row, ...patch } : row
                          ),
                      }
                    : station
            )
        );
    };

    const canApply = unmatchedCount === 0 && !submitting;

    const handleApply = async () => {
        if (!canApply) return;
        setSubmitting(true);
        setError(null);
        try {
            const payload: ConveyorDrawingApplyPayload = {
                stations: stations.map((station) => ({
                    station_type_id: station.station_type_id,
                    name: `${station.code}${
                        station.description ? ` — ${station.description}` : ''
                    }`,
                    comments: null,
                    selections: station.rows
                        .filter((row) => !row.removed && row.target_id)
                        .map((row): DrawingRowSelection =>
                            row.kind === 'process'
                                ? {
                                      kind: 'process',
                                      process_template_id: row.target_id as string,
                                      quantity: row.quantity,
                                  }
                                : {
                                      kind: 'equipment',
                                      equipment_id: row.target_id as string,
                                      quantity: row.quantity,
                                      is_free_issued: false,
                                  }
                        ),
                })),
            };
            await onApply(payload);
        } catch (err) {
            const detail =
                (err as { response?: { data?: { detail?: string } } })?.response?.data
                    ?.detail ?? (err instanceof Error ? err.message : 'Failed to apply drawing');
            setError(detail);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#141414]/60 px-4"
        >
            <div className="relative w-full max-w-5xl max-h-[85vh] bg-[#f3efe6] border border-[#141414] shadow-[0_3px_0_0_#141414] flex flex-col">
                <span className="absolute -top-px -left-px h-3 w-3 border-t-2 border-l-2 border-[#141414]" />
                <span className="absolute -top-px -right-px h-3 w-3 border-t-2 border-r-2 border-[#141414]" />
                <span className="absolute -bottom-px -left-px h-3 w-3 border-b-2 border-l-2 border-[#141414]" />
                <span className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-[#141414]" />

                <header className="px-6 py-4 border-b border-[#141414]/15 flex items-start justify-between gap-4">
                    <div>
                        <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#e0461e]">
                            Apply drawing
                        </div>
                        <h2 className="ff-serif text-2xl leading-none mt-1">
                            Confirm stations & equipment
                        </h2>
                        <div className="ff-mono text-[11px] text-[#141414]/55 mt-2">
                            Replaces existing stations. Steps from existing stations move to the first new station.
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="ff-mono text-[10px] uppercase tracking-wider text-[#141414]/55 hover:text-[#141414]"
                    >
                        Close
                    </button>
                </header>

                <div className="px-6 py-3 border-b border-[#141414]/15 ff-mono text-[11px] text-[#141414]/70 flex flex-wrap gap-x-6 gap-y-1">
                    <span>
                        {stations.length} station{stations.length === 1 ? '' : 's'}
                    </span>
                    <span>
                        {equipmentLineCount} equipment line{equipmentLineCount === 1 ? '' : 's'}
                    </span>
                    {unmatchedCount > 0 && (
                        <span className="text-[#c2251a]">
                            {unmatchedCount} unmatched — pick a catalog item or remove
                        </span>
                    )}
                </div>

                <div className="overflow-y-auto px-6 py-4 space-y-6">
                    {stations.map((station, sIdx) => (
                        <section
                            key={`${station.code}-${sIdx}`}
                            className="border border-[#141414]/15 bg-white"
                        >
                            <div className="px-4 py-3 border-b border-[#141414]/10 flex flex-wrap items-center gap-4">
                                <div className="min-w-0 grow">
                                    <div className="ff-mono text-xs text-[#141414]">
                                        {station.code}
                                    </div>
                                    {station.description && (
                                        <div className="ff-mono text-[10px] text-[#141414]/55">
                                            {station.description}
                                        </div>
                                    )}
                                </div>
                                <label className="ff-mono text-[10px] uppercase tracking-wider text-[#141414]/70 flex items-center gap-2">
                                    Station type
                                    <select
                                        value={station.station_type_id}
                                        onChange={(e) =>
                                            updateStation(sIdx, {
                                                station_type_id: e.target.value,
                                            })
                                        }
                                        className="ff-mono text-xs border border-[#141414]/25 bg-white px-2 py-1"
                                    >
                                        {stationTypes.map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            {station.rows.length === 0 ? (
                                <div className="px-4 py-3 ff-mono text-xs text-[#141414]/55">
                                    No equipment found near this station.
                                </div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="ff-mono text-[10px] uppercase tracking-wider text-[#141414]/55 border-b border-[#141414]/10">
                                            <th className="text-left px-4 py-2 font-normal">
                                                Drawing label
                                            </th>
                                            <th className="text-left px-4 py-2 font-normal">
                                                Maps to
                                            </th>
                                            <th className="text-left px-4 py-2 font-normal w-20">
                                                Qty
                                            </th>
                                            <th className="px-4 py-2 w-12"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {station.rows.map((row, rIdx) => {
                                            const isUnmatched =
                                                !row.removed && !row.target_id;
                                            const selectValue = row.target_id
                                                ? encodeTarget(row.kind, row.target_id)
                                                : '';
                                            return (
                                                <tr
                                                    key={`${row.block_name}-${rIdx}`}
                                                    className={`border-b border-[#141414]/5 ${
                                                        row.removed
                                                            ? 'opacity-40 line-through'
                                                            : isUnmatched
                                                              ? 'bg-[#e0461e]/5'
                                                              : ''
                                                    }`}
                                                >
                                                    <td className="px-4 py-2 align-top">
                                                        <div className="ff-mono text-xs text-[#141414]">
                                                            {row.block_name}
                                                        </div>
                                                        {row.robot_codes.length > 0 && (
                                                            <div className="ff-mono text-[10px] text-[#141414]/55">
                                                                {row.robot_codes.join(', ')}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2 align-top">
                                                        <select
                                                            value={selectValue}
                                                            disabled={row.removed}
                                                            onChange={(e) => {
                                                                const decoded = decodeTarget(
                                                                    e.target.value
                                                                );
                                                                if (!decoded) {
                                                                    updateRow(sIdx, rIdx, {
                                                                        target_id: null,
                                                                    });
                                                                } else {
                                                                    updateRow(sIdx, rIdx, {
                                                                        kind: decoded.kind,
                                                                        target_id: decoded.id,
                                                                    });
                                                                }
                                                            }}
                                                            className="ff-mono text-xs border border-[#141414]/25 bg-white px-2 py-1 w-full max-w-md"
                                                        >
                                                            <option value="">
                                                                — Select equipment or process —
                                                            </option>
                                                            {catalog.length > 0 && (
                                                                <optgroup label="Equipment">
                                                                    {catalog.map((item) => (
                                                                        <option
                                                                            key={`equipment-${item.id}`}
                                                                            value={encodeTarget(
                                                                                'equipment',
                                                                                item.id
                                                                            )}
                                                                        >
                                                                            {item.name}
                                                                        </option>
                                                                    ))}
                                                                </optgroup>
                                                            )}
                                                            {processTemplates.length > 0 && (
                                                                <optgroup label="Processes">
                                                                    {processTemplates.map(
                                                                        (template) => (
                                                                            <option
                                                                                key={`process-${template.id}`}
                                                                                value={encodeTarget(
                                                                                    'process',
                                                                                    template.id
                                                                                )}
                                                                            >
                                                                                {template.name}
                                                                            </option>
                                                                        )
                                                                    )}
                                                                </optgroup>
                                                            )}
                                                        </select>
                                                        {row.target_id &&
                                                            row.match_confidence > 0 &&
                                                            row.match_confidence < 1 && (
                                                                <div className="ff-mono text-[10px] text-[#141414]/55 mt-1">
                                                                    auto-match · {Math.round(
                                                                        row.match_confidence * 100
                                                                    )}
                                                                    % confidence
                                                                    {row.kind === 'process'
                                                                        ? ' · process'
                                                                        : ''}
                                                                </div>
                                                            )}
                                                        {row.target_id &&
                                                            row.kind === 'process' &&
                                                            row.match_confidence >= 1 && (
                                                                <div className="ff-mono text-[10px] text-[#141414]/55 mt-1">
                                                                    process · expands on apply
                                                                </div>
                                                            )}
                                                    </td>
                                                    <td className="px-4 py-2 align-top">
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            value={row.quantity}
                                                            disabled={row.removed}
                                                            onChange={(e) =>
                                                                updateRow(sIdx, rIdx, {
                                                                    quantity: Math.max(
                                                                        1,
                                                                        Number(e.target.value) || 1
                                                                    ),
                                                                })
                                                            }
                                                            className="ff-mono text-xs border border-[#141414]/25 bg-white px-2 py-1 w-16"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-2 align-top text-right">
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                updateRow(sIdx, rIdx, {
                                                                    removed: !row.removed,
                                                                })
                                                            }
                                                            className="ff-mono text-[10px] uppercase tracking-wider text-[#141414]/55 hover:text-[#c2251a]"
                                                            title={
                                                                row.removed
                                                                    ? 'Undo remove'
                                                                    : 'Remove'
                                                            }
                                                        >
                                                            {row.removed ? 'Undo' : 'Remove'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </section>
                    ))}

                    {layout.unassigned_equipment.length > 0 && (
                        <section className="border border-[#141414]/15 bg-white">
                            <div className="px-4 py-3 ff-mono text-[10px] uppercase tracking-wider text-[#141414]/55">
                                Unassigned equipment ({layout.unassigned_equipment.length})
                            </div>
                            <ul className="px-4 pb-3 ff-mono text-xs text-[#141414]/70 space-y-1">
                                {layout.unassigned_equipment.map((c, i) => (
                                    <li key={i}>
                                        × {c.quantity} {c.block_name}
                                    </li>
                                ))}
                            </ul>
                            <div className="px-4 pb-3 ff-mono text-[10px] text-[#141414]/55">
                                Not attached to any station — add manually later if needed.
                            </div>
                        </section>
                    )}

                    {layout.warnings.length > 0 && (
                        <section className="border border-[#141414]/15 bg-[#141414]/[0.03] px-4 py-3">
                            <div className="ff-mono text-[10px] uppercase tracking-wider text-[#141414]/55 mb-1">
                                Parser warnings
                            </div>
                            <ul className="ff-mono text-[11px] text-[#141414]/70 space-y-1">
                                {layout.warnings.map((w, i) => (
                                    <li key={i}>{w}</li>
                                ))}
                            </ul>
                        </section>
                    )}
                </div>

                {error && (
                    <div className="px-6 py-2 border-t border-[#141414]/15 ff-mono text-xs text-[#c2251a]">
                        {error}
                    </div>
                )}

                <footer className="px-6 py-4 border-t border-[#141414]/15 flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={submitting}
                        className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 border border-[#141414]/25 hover:bg-[#141414]/5 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={!canApply}
                        className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 bg-[#141414] text-[#f3efe6] hover:bg-[#e0461e] disabled:opacity-50 transition"
                    >
                        {submitting ? 'Applying…' : 'Apply drawing'}
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default DxfMatchingDialog;
